import * as Sentry from '@sentry/react';
import React from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

import ignoreErrors from 'hb-react/shared/utils/errorReporting/ignoreErrors';
import {
  setBreadcrumbLegacyJSResponse,
  setEventLegacyJSResponse,
} from 'hb-react/shared/utils/errorReporting/legacyJSResponse';
import setOfficeTag from 'hb-react/shared/utils/errorReporting/setOfficeTag';
import { shouldIgnoreOutdatedChrome } from 'hb-react/shared/utils/errorReporting/utils';

let isInit = false;

// Hardcoded value to ignore errors from old Chrome versions
// Choice of PS team to maintain this version manually and not dynamically
// to avoid call to external API (to get the lastest of Chrome)
export const CHROME_MINIMUM_VERSION = 127;

export const initSentry = (envs) => {
  if (envs.SENTRY_DSN_JAVASCRIPT && envs.CLUSTER_NAME) {
    Sentry.init({
      dsn: envs.SENTRY_DSN_JAVASCRIPT,
      environment: envs.CLUSTER_NAME,
      release: envs.APP_VERSION,

      // Add stacktrace on all message
      attachStacktrace: true,
      integrations: [
        Sentry.thirdPartyErrorFilterIntegration({
          filterKeys: [
            envs.SENTRY_APPLICATION_KEY ?? 'hivebrite-alumni_connect_front',
          ],
          behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
        }),
        Sentry.reactRouterV6BrowserTracingIntegration({
          enableInp: true,
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.launchDarklyIntegration(),
      ],
      tracesSampleRate: 1.0,
      ignoreErrors,

      beforeBreadcrumb(breadcrumb, hint) {
        setBreadcrumbLegacyJSResponse(breadcrumb, hint);

        return breadcrumb;
      },
      beforeSendTransaction: (event) => {
        setOfficeTag(event);

        return event;
      },
      beforeSend: (event) => {
        if (shouldIgnoreOutdatedChrome(CHROME_MINIMUM_VERSION)) {
          return null;
        }
        // Add connection type as tag to the current event
        // This information is avalaible only with chrome and edge.
        if ('connection' in navigator) {
          const { connection } = navigator as Navigator;

          // effectiveType can be "2g" | "3g" | "4g" | "slow-2g"
          if (connection?.effectiveType) {
            event.tags = {
              ...(event.tags || {}),
              effectiveConnectionType: connection.effectiveType,
            };
          }

          // type can be "bluetooth" | "cellular" | "ethernet" | "mixed" | "none" | "other" | "unknown" | "wifi" | "wimax"
          if (connection?.type) {
            event.tags = {
              ...(event.tags || {}),
              connectionType: connection.type,
            };
          }
        }

        // Override default filename origin
        // @ts-expect-error: fix this later
        event?.stacktrace?.frames?.forEach((frame) => {
          const { pathname } = new URL(frame.filename);

          frame.filename = `~${pathname}`;
        });

        setEventLegacyJSResponse(event);
        setOfficeTag(event);

        return event;
      },
    });

    isInit = true;
  }
};

export const setContext = ({ user, network }) => {
  if (user) {
    Sentry.setUser(user);
  }

  if (network) {
    Sentry.setExtras({ network });
    Sentry.setTag('network', network.id);
    Sentry.setTag('network.identifier', network.identifier);
  }
};

const SentryLogger = {
  captureMessage: (message) => isInit && Sentry.captureMessage(message),
  captureException: (err, context) =>
    isInit && Sentry.captureException(err, context),
};

const DevLogger = {
  // eslint-disable-next-line no-console
  captureMessage: (message) => console.trace(message),
  // eslint-disable-next-line no-console
  captureException: (err, context) => console.trace(err, context),
};

export const isDevEnvironment = process.env.NODE_ENV === 'development';
export const isTestEnvironment = process.env.NODE_ENV === 'test';

const Logger = isInit ? SentryLogger : DevLogger;

export const captureMessage = (message: string) =>
  Logger.captureMessage(message);

export const captureException = (err: any, context?: any) =>
  Logger.captureException(err, context);
